import React, { FC, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import {
  AmountInput,
  Button,
  CurrenciesOrdered,
  CurrenciesUSD,
  Dropdown,
  DropdownOption,
  Modal,
  ModalSize,
  formatNumber,
} from '@column/column-ui-kit';

import { Notification } from '~/components';
import { NotificationList } from '~/components/NotificationList';
import { SimulateRepository } from '~/repositories';
import { BankAccountRepository } from '~/repositories/BankAccountRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';

const Form = styled.form`
  display: grid;
  grid-gap: 24px;
  padding: 24px;
`;

const Fields = styled.div`
  display: grid;
  grid-gap: 16px;
`;

const FormWrapper = styled.div`
  --toggle-height-padding: 0 0 16px 0;
`;

export interface SimulateTransferModalProps {
  isOpen: boolean;
  onClose: () => void;
  accountNumberId?: string;
  onSuccess?: () => void;
}

export const SimulateTransferModal: FC<SimulateTransferModalProps> = ({
  isOpen,
  onClose,
  accountNumberId: initialAccountNumberId,
  onSuccess,
}) => {
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const currentUser = useSessionStore((state) => state.currentUser);

  const [accountNumberId, setAccountNumberId] = useState<string>(initialAccountNumberId || '');
  const [type, setType] = useState<string>('ach-credit');
  const [amount, setAmount] = useState<number | undefined>(undefined);
  const [currency, setCurrency] = useState<string>('USD');
  const [bankAccountList, setBankAccountList] = useState<DropdownOption[]>([]);

  const handlePromiseError = useCallback(
    async (promise: Promise<any>, name: string) => {
      return promise
        .then(() => {
          onClose();

          addSuccessNotification({
            content: `${name} sent`,
          });

          onSuccess?.();
        })
        .catch((error: any) => {
          addDangerNotification({
            content: error.message,
            display: 'popup',
          });
        });
    },
    [onClose, onSuccess, addSuccessNotification, addDangerNotification]
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      if (!accountNumberId) {
        addDangerNotification({
          content: 'Account number id not found',
          display: 'popup',
        });
        return;
      }

      if (type === 'ach-credit') {
        handlePromiseError(
          SimulateRepository.receiveACHCredit({
            destinationAccountNumberId: accountNumberId,
            amount: (amount ?? 0).toString(),
            currencyCode: currency ?? '',
          }),
          'ACH Credit'
        );
        return;
      }

      if (type === 'ach-debit') {
        handlePromiseError(
          SimulateRepository.receiveACHDebit({
            destinationAccountNumberId: accountNumberId,
            amount: (amount ?? 0).toString(),
            currencyCode: currency ?? '',
          }),
          'ACH Debit'
        );
        return;
      }

      if (type === 'wire') {
        handlePromiseError(
          SimulateRepository.receiveWire({
            destinationAccountNumberId: accountNumberId,
            amount: (amount ?? 0).toString(),
            currencyCode: currency ?? '',
          }),
          'Wire Transfer'
        );
        return;
      }

      if (type === 'international-wire') {
        handlePromiseError(
          SimulateRepository.receiveInternationalWire({
            destinationAccountNumberId: accountNumberId,
            amount: (amount ?? 0).toString(),
            currencyCode: currency ?? '',
          }),
          'International Wire Transfer'
        );
        return;
      }

      if (type === 'realtime') {
        handlePromiseError(
          SimulateRepository.createRealtimeTransfer({
            destinationAccountNumberId: accountNumberId,
            amount: (amount ?? 0).toString(),
            currencyCode: currency ?? '',
          }),
          'Realtime Transfer'
        );
      }
    },
    [accountNumberId, type, amount, currency, handlePromiseError, addDangerNotification]
  );

  const fetchBankAccounts = useCallback(
    async (search?: string) => {
      let result: any = [];

      try {
        const response = await BankAccountRepository.getAll({ description: search });
        result = response.bankAccounts;
      } catch (error) {
        addDangerNotification({
          content: error as string,
        });
      }

      const entries: DropdownOption[] = [];
      result.map((entry: any) => {
        entries.push({
          label: `${
            entry?.displayName && entry?.description
              ? `${entry.displayName} – ${entry.description}`
              : entry?.description || 'Unnamed'
          } (${formatNumber(entry?.balances?.availableAmount)})`,
          small: entry.id,
          value: entry.defaultAccountNumberId,
        });
      });

      setBankAccountList(entries);
    },
    [addDangerNotification]
  );

  useEffect(() => {
    if (isOpen) {
      setAccountNumberId(initialAccountNumberId || '');
      setType('ach-credit');
      setAmount(undefined);
      setCurrency('USD');

      if (currentUser) {
        fetchBankAccounts();
      }
    }
  }, [isOpen, initialAccountNumberId, currentUser, fetchBankAccounts]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={ModalSize.Small}>
      <Modal.IconCircle icon="AnimationPiggy" type="primary" />

      <Modal.Content beforeContent={<NotificationList display="popup" variant="light" />}>
        <Modal.Headline align="center">Sandbox</Modal.Headline>
        <Modal.Paragraph align="center">Simulate ACH, Wire, International Wire, and Realtime transfers</Modal.Paragraph>
      </Modal.Content>

      <Form onSubmit={handleSubmit}>
        <FormWrapper>
          <Notification
            variant="light"
            color="info"
            withClose={false}
            isClose={type === 'wire' || type === 'international-wire' || type === 'realtime'}
          >
            Simulated ACH transfers may take 5-10 minutes to show up.
          </Notification>
          <Fields>
            {!initialAccountNumberId && (
              <Dropdown
                options={bankAccountList}
                active={accountNumberId ?? ''}
                fullWidth
                maxWidth="640px"
                search
                searchLabel="Search for description..."
                onSearchChange={(value: string) => fetchBankAccounts(value)}
                onChange={setAccountNumberId}
              />
            )}
            <Dropdown
              onChange={(value: string) => {
                setType(value);
                setCurrency('USD');
              }}
              active={type}
              options={[
                {
                  label: 'Receive ACH Credit',
                  value: 'ach-credit',
                },
                {
                  label: 'Receive ACH Debit',
                  value: 'ach-debit',
                },
                {
                  label: 'Receive Wire',
                  value: 'wire',
                },
                {
                  label: 'Receive International Wire',
                  value: 'international-wire',
                },
                {
                  label: 'Receive Realtime Transfer',
                  value: 'realtime',
                },
              ]}
              fullWidth
            />
            <AmountInput
              onChange={setAmount}
              onCurrencyCodeChange={setCurrency}
              currencyCode={currency}
              currencyList={type === 'international-wire' ? CurrenciesOrdered : CurrenciesUSD}
              value={amount}
            />
          </Fields>
        </FormWrapper>
        <Button>Send</Button>
      </Form>
    </Modal>
  );
};
